import { z } from 'zod';

/**
 * Account deletion (DECISIONS 2026-10-27 D10, review S2): the signed-in user deletes their own
 * account at once, after typing the confirmation word; the school's admins are told. A Tenant
 * Admin can delete another user through the same server service.
 */
export const ME_PATH = '/api/v1/me';
export const ADMIN_USER_DELETE_PATH = '/api/v1/admin/users/:userId';

/** The word the user types on the confirmation screen; the server checks it again. */
export const ACCOUNT_DELETION_CONFIRM_WORD = 'DELETE';

/**
 * The access token's `auth_time` must be at most this old (10 minutes). Otherwise the server
 * answers 403 RECENT_SIGN_IN_REQUIRED and the app signs the user in again (OIDC `prompt=login`
 * or `max_age`) before retrying.
 */
export const ACCOUNT_DELETION_MAX_AUTH_AGE_SEC = 600;

/** Body of DELETE /api/v1/me and DELETE /api/v1/admin/users/:userId. */
export const deleteMeRequestSchema = z
  .object({ confirm: z.literal(ACCOUNT_DELETION_CONFIRM_WORD) })
  .strict();
export type DeleteMeRequest = z.infer<typeof deleteMeRequestSchema>;

/** 200 response: the account and its data are gone; the app wipes the school and signs out. */
export const accountDeletedSchema = z.object({
  deleted: z.literal(true),
  deletedAt: z.iso.datetime(),
});
export type AccountDeleted = z.infer<typeof accountDeletedSchema>;

/** True when `authTimeSec` (token `auth_time`, seconds) is within the allowed age of `nowMs`. */
export function isRecentSignIn(
  authTimeSec: number | undefined,
  nowMs: number,
  maxAgeSec: number = ACCOUNT_DELETION_MAX_AUTH_AGE_SEC,
): boolean {
  if (authTimeSec === undefined || !Number.isFinite(authTimeSec)) return false;
  const ageSec = nowMs / 1000 - authTimeSec;
  // A sign-in "in the future" beyond small clock skew is not trusted.
  return ageSec >= -60 && ageSec <= maxAgeSec;
}
